import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { User, Camera, Globe, Save, Eye, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const UserSettingsPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_display_name: user?.user_display_name || '',
    user_description: user?.user_description || '',
    user_social_url: user?.user_social_url || '',
    contact_email: user?.contact_email || '',
    available_for_hire: user?.available_for_hire || false
  });

  const [profileImage, setProfileImage] = useState(null);
  
  // Construct proper profile image URL
  const getProfileImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('/api/')) return `${BACKEND_URL}${imageUrl}`;
    return imageUrl;
  };
  
  const [profileImagePreview, setProfileImagePreview] = useState(getProfileImageUrl(user?.user_image_url));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError('Profile image must be less than 2MB');
        return;
      }
      // Client-side 300x300 minimum check for fast feedback (backend re-validates)
      const probe = new Image();
      probe.onload = () => {
        if (probe.width < 300 || probe.height < 300) {
          setError(`Profile image must be at least 300x300 pixels (your image is ${probe.width}x${probe.height})`);
          return;
        }
        setProfileImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
        setError(null);
      };
      probe.onerror = () => setError('Unable to read image. Please try a different file.');
      probe.src = URL.createObjectURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const updateData = new FormData();
      updateData.append('user_display_name', formData.user_display_name);
      updateData.append('user_description', formData.user_description);
      updateData.append('user_social_url', formData.user_social_url);
      updateData.append('contact_email', formData.contact_email);
      updateData.append('available_for_hire', formData.available_for_hire ? 'true' : 'false');
      
      if (profileImage) {
        updateData.append('profile_image', profileImage);
      }

      await axios.put(`${API_BASE}/users/profile`, updateData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage('Profile updated successfully!');
      setProfileImage(null); // Reset file input state
      await checkAuth(); // Refresh user data

      setTimeout(() => {
        setMessage(null);
      }, 3000);

    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Settings</h1>
          <p className="text-gray-600">Manage your public profile information</p>
        </div>

        {/* Messages */}
        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-900">{message}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-900">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Camera className="w-5 h-5" />
              <span>Profile Photo</span>
            </h2>
            
            <div className="flex items-center space-x-6">
              <div className="relative">
                {profileImagePreview ? (
                  <img
                    src={profileImagePreview}
                    alt={`${user?.user_display_name || 'My'} Photographer on Explora Mauritius`}
                    className="w-24 h-24 rounded-full object-cover ring-4 ring-gray-100"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-gray-100">
                    <User className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('profile-file-input').click()}
                  className="mb-2"
                >
                  Choose file
                </Button>
                <input
                  id="profile-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                {profileImage && (
                  <p className="text-xs text-green-600 mb-1">{profileImage.name}</p>
                )}
                <p className="text-xs text-gray-500">JPG, PNG, WebP - Max 2MB. Photo should be 1:1 ratio, at least 300px by 300px.</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>Basic Information</span>
            </h2>

            {/* Display Name */}
            <div>
              <Label htmlFor="user_display_name">Display Name *</Label>
              <Input
                id="user_display_name"
                name="user_display_name"
                type="text"
                value={formData.user_display_name}
                onChange={handleInputChange}
                required
                className="mt-1"
                placeholder="Your name"
              />
            </div>

            {/* Login Email (Read-only) */}
            <div>
              <Label htmlFor="user_email" className="flex items-center space-x-1">
                <Lock className="w-3 h-3" />
                <span>Login Email</span>
              </Label>
              <div className="flex items-center space-x-2 mt-1">
                <Mail className="w-5 h-5 text-gray-400" />
                <Input
                  id="user_email"
                  type="email"
                  value={user?.user_email || ''}
                  disabled
                  className="flex-1 bg-gray-100 cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">This is the Google account you use to sign in. It cannot be changed.</p>
            </div>

            {/* Contact Email (Editable) */}
            <div>
              <Label htmlFor="contact_email">Contact Email</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Mail className="w-5 h-5 text-gray-400" />
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  placeholder="your-preferred@email.com"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">We'll use this email to communicate with you. Leave empty to use your login email.</p>
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="user_description">Bio</Label>
              <Textarea
                id="user_description"
                name="user_description"
                value={formData.user_description}
                onChange={handleInputChange}
                rows={4}
                className="mt-1"
                placeholder="Tell us about yourself and your photography..."
              />
              <p className="text-xs text-gray-500 mt-1">HTML is supported</p>
            </div>

            {/* Social URL */}
            <div>
              <Label htmlFor="user_social_url">Social Profile URL</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Globe className="w-5 h-5 text-gray-400" />
                <Input
                  id="user_social_url"
                  name="user_social_url"
                  type="url"
                  value={formData.user_social_url}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/yourname"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Your Instagram, Facebook, or personal website</p>
            </div>

            {/* Available for Hire */}
            <div className="pt-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.available_for_hire}
                  onChange={(e) => setFormData({ ...formData, available_for_hire: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  data-testid="available-for-hire-checkbox"
                />
                <span className="text-sm font-medium text-gray-700">Available for hire</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-8">Check this if you want visitors to know you're available for photography work. If unchecked, your profile will show "Hobbyist photographer".</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <div className="flex items-center space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !formData.user_display_name.trim()}
                className="min-w-[150px]"
              >
                {saving ? (
                  <>
                    <Save className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSettingsPage;